import axios, {AxiosError} from "axios";
import config from "../../config/default.json";
import {hideLoader, showAlert, showError} from "../../helpers/ComponentHelper";
import {sleep} from "../../helpers/DateHelper";
import Menu, {Recommendation} from "../../@types/stores/menu";
import ApiGlobalSettings from "../../@types/api/settings";
import ApiOrder, {ApiCombinationOrderLine, ApiHalfAndHalfOrderLine, ApiOrderLine} from "../../@types/api/order";
import {CashRegisterPaymentMethod, Payment, PaymentStatus, PaymentTerminal} from "../../@types/api/payment";
import i18next, {t} from "i18next";
import pack from "../../../package.json";
import {Buffer} from 'buffer';
import LayoutStyles from "../../@types/api/styles";
import {PaymentMethod} from "../../@types/enums/PaymentType";
import orderStore from "../../stores/order";
import ApiCustomer from "../../@types/api/customer";
import moment from "moment";
import ApiFilter from "../../@types/api/filter";
import {addToLog, clearLogs, getLogs, isRetail, isSendLog} from "../../helpers/ApiHelper";
import lodash from "lodash";
import {LogLevel} from "../../@types/enums/logLevel";
import ApiInternalCard from "../../@types/api/internalCard";


const authLimit = config.api.authLimit;
const authDelay = config.api.authDelay;

axios.defaults.headers['Content-Type'] = 'application/json;charset=utf-8';
axios.defaults.headers['Access-Control-Allow-Origin'] = '*';
axios.defaults.withCredentials = false;
axios.defaults.timeout = 30000;


const allowedStatusCode = [304];

class OpenApi {
    host: string = "";
    code: string = "";
    cashRegisterId: string = "";
    siteId: string = "";
    authTrys = 0;
    userErrors: number[] = [5, 8, 9, 16, 17, 19, 25, 26, 27, 28, 29];
    /*
    None = 0,
    OrderIsEmpty = 1,
    ArticleNumberNotFound = 2,
    OrderAlreadyCreated = 3,
    PaymentAlreadyAdded = 4,
    WrongAmount = 5,
    WrongSellFactor = 6,
    WrongTerminalId = 7,
    WrongQuantity = 8,
    WrongDiscount = 9,
    WrongPrintStrategy = 10,
    WrongPaymentMethod = 11,
    InternalCardHasInsufficientBalance = 12,
    WrongCashRegister = 13,
    WrongEmployee = 14,
    WrongWebhook = 15,
    WrongInternalCardNumber = 16,
    PaymentProcessError = 17,?
    PaymentProcessError = 17,?
    ElectronicReceiptsIsDisabled = 18,
    InternalCardNotFound = 19,
    OutOfStock = 20,
    OrderIsSealed = 21,
    OrderWasNotFound = 22,
    OrderLineWasNotFound = 23,
    OrderForbidden = 24,
    CustomerNotFound = 25,
    CardNumberWasNotFound = 26,
    ArticleCouldNotBeSellAtThisTime = 27,
    PaymentProcessCancelled = 28,
    PaymentCouldNotBeCancelled = 29,
    UnknownError = 999
    */


    constructor() {
        this.init();
    }

    init() {
        this.host = localStorage.getItem("host") || "";
        this.code = localStorage.getItem("code") || "";
        this.siteId = localStorage.getItem("siteId") || "";
        this.cashRegisterId = localStorage.getItem("cashRegisterId") || "";

        const token = localStorage.getItem("token") || "";
        if (token) return;

        if (this.host && this.code && this.cashRegisterId)
            this.reAuth();
    }

    async run(fn?: Function, params?: any) {
        if (!fn) return;

        const arrParams = Object.values(params || {});
        await fn.call(this, ...arrParams);
    }

    private async checkAuth(err: AxiosError, fn?: Function, params?: any) {
        if (err.status === 401) {
            if (this.authTrys >= authLimit) {
                console.error("Auth limit");
                showError("Can't connect to the API");
                return;
            }
            else {
                console.log(`Try to api re-auth. Try = ${this.authTrys + 1}`);
                if (this.authTrys > 0)
                    await sleep(authDelay * (this.authTrys + 1));

                this.authTrys++;
                const r = await this.reAuth();
                if (r) {
                    if (fn) {
                        await this.run(fn, params);
                        return;
                    }
                }
                else {
                    await this.run(fn, params);
                    return;
                }
            }
        }
        else if (err.status === 400 && err.response?.data) {
            const data = err as AxiosError<{errorCode: number}>;
            if (data?.response?.data?.errorCode === 42) {
                showAlert(i18next.t("somethingWentWrong"), i18next.t("tooManyRequests"));
            }
        }
        else if (allowedStatusCode.some(x => x === err.status)) {
            return;
        }
        else {
            showError(t("error.apiConnection"));
        }
    }

    private async reAuth(): Promise<boolean> {
        localStorage.removeItem("token");
        const token = await this.getTokenFromApi();
        if (token) {
            return true;
        }
        return false;
    }

    private async getToken(): Promise<string | null> {
        let token = localStorage.getItem("token");
        if (!token)
            token = await this.getTokenFromApi();

        return token;
    }

    private async getTokenFromApi(): Promise<string | null> {
        try {
            if (this.host && this.code && this.cashRegisterId) {
                const siteParam = this.siteId ? `&siteid=${this.siteId}` : "";
                const r = await axios.post(`${this.host}/api/integration/auth/cash-register/${this.cashRegisterId}?${siteParam}`, {
                    code: this.code,
                    appVersion: pack.version,
                    cashRegisterType: 2
                });
                if (r.data) {
                    this.authTrys = 0;
                    localStorage.setItem("token", r.data);

                    return r.data || undefined;
                }
            }
        }
        catch (e) {
            showError((e as Error).toString());
            console.error(e);
        }
        return null;
    }

    private matchUserError(r: AxiosError<any>): boolean {
        if (typeof r?.response?.data?.errorCode === "number" && this.userErrors.some(x => x === r.response?.data.errorCode)) {
            showAlert(t(`error.customer.${r.response.data.errorCode}.title`), t(`error.customer.${r.response.data.errorCode}.text`));
            return true;
        }

        return this.errorProcessing(r.status || -1, r.response?.data.errorCode || -1);
    }

    private errorProcessing(errorStatus: number, errorCode: number): boolean {
        if (errorStatus === 429) {
            hideLoader();
            showAlert(i18next.t("somethingWentWrong"), i18next.t("tooManyRequests"));
            return true;
        }
        else if (errorStatus === 400  && errorCode === 20) {
            showAlert(i18next.t("error.customer.20.title"), i18next.t("error.customer.20.text"));
            return true;
        }

        return false;
    }

    public async getEatInMenu(refresh: boolean = false): Promise<Menu[] | null> {
        try {
            const token = await this.getToken();

            let etag = "";
            if (!refresh) {
                etag = `&etag=${encodeURIComponent(localStorage.getItem("eatIn_etag") || "")}`;
            }

            const r = await axios.get<Menu[]>(`${this.host}/api/integration/articles/eat-in?includeCombinations=true&includeSellInGlass=true${etag}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            localStorage.setItem("eatIn_etag", r.headers.etag || "");
            localStorage.setItem("eatIn_menu", JSON.stringify(r.data));

            return r.data;
        }
        catch (e) {
            const errAxios = e as AxiosError;
            if (errAxios?.response) {
                await this.checkAuth(errAxios, this.getEatInMenu, { refresh });

                // if 304 - not modified
                if (errAxios.status === 304) {
                    try {
                        const menuString = localStorage.getItem("eatIn_menu");
                        if (menuString)
                            return JSON.parse(menuString) as Menu[];
                    }
                    catch (e) {
                        console.error(e);
                    }

                    localStorage.removeItem("eatIn_menu");
                    return await this.getEatInMenu(true);
                }
            }
            else {
                showError((e as Error).toString());
                console.error(e);
            }
        }
        return null;
    }

    public async getTakeAwayMenu(refresh: boolean = false): Promise<Menu[] | null> {
        try {
            const token = await this.getToken();

            let etag = "";
            if (!refresh) {
                etag = `&etag=${encodeURIComponent(localStorage.getItem("takeAway_etag") || "")}`;
            }

            const includeHidden = isRetail() ? "&includeHidden=true" : "";

            const r = await axios.get<Menu[]>(`${this.host}/api/integration/articles/takeaway?includeCombinations=true&includeSellInGlass=true${includeHidden}${etag}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            localStorage.setItem("takeAway_etag", r.headers.etag || "");
            localStorage.setItem("takeAway_menu", JSON.stringify(r.data));

            return r.data;
        }
        catch (e) {
            const errAxios = e as AxiosError;
            if (errAxios?.response) {
                await this.checkAuth(errAxios, this.getTakeAwayMenu, { refresh });
                // if 304 - not modified
                if (errAxios.status === 304) {
                    try {
                        const menuString = localStorage.getItem("takeAway_menu");
                        if (menuString)
                            return JSON.parse(menuString) as Menu[];
                    }
                    catch (e) {
                        console.error(e);
                    }

                    localStorage.removeItem("takeAway_menu");
                    return await this.getTakeAwayMenu(true);
                }
            }
            else {
                showError((e as Error).toString());
                console.error(e);
            }
        }
        return null;
    }

    public async getEatInRecommendations(force: boolean = false): Promise<Recommendation[] | null> {
        try {
            const token = await this.getToken();

            const etag = force ? "" : `?etag=${encodeURIComponent(localStorage.getItem("eatInRecommendations_etag") || "")}`;

            const r = await axios.get<Recommendation[]>(`${this.host}/api/integration/recommendations/eat-in${etag}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            localStorage.setItem("eatInRecommendations_etag", r.headers.etag || "");

            return r.data;
        }
        catch (e) {
            const errAxios = e as AxiosError;
            if (errAxios?.response) {
                await this.checkAuth(errAxios, this.getEatInRecommendations, { force });
            }
            else {
                showError((e as Error).toString());
                console.error(e);
            }
        }
        return null;
    }

    public async getTakeAwayRecommendations(force: boolean = false): Promise<Recommendation[] | null> {
        try {
            const token = await this.getToken();

            const etag = force ? "" : `?etag=${encodeURIComponent(localStorage.getItem("takeAwayRecommendations_etag") || "")}`;

            const r = await axios.get<Recommendation[]>(`${this.host}/api/integration/recommendations/takeaway${etag}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            localStorage.setItem("takeAwayRecommendations_etag", r.headers.etag || "");

            return r.data;
        }
        catch (e) {
            const errAxios = e as AxiosError;
            if (errAxios?.response) {
                await this.checkAuth(errAxios, this.getTakeAwayRecommendations, { force });
            }
            else {
                showError((e as Error).toString());
                console.error(e);
            }
        }
        return null;
    }

    public async getGlobalSettings(): Promise<ApiGlobalSettings | null> {
        try {
            if (!this.host) return null;

            const token = await this.getToken();

            const r = await axios.get<ApiGlobalSettings>(`${this.host}/api/integration/settings`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            return r.data;
        }
        catch (e) {
            const errAxios = e as AxiosError;
            if (errAxios?.response) {
                await this.checkAuth(errAxios, this.getGlobalSettings);
            }
            else {
                showError((e as Error).toString());
                console.error(e);
            }
        }
        return null;
    }

    public async createOrder(order: ApiOrder): Promise<ApiOrder | null> {
        try {
            const token = await this.getToken();

            addToLog(`-> PUT /api/integration/orders \n ${JSON.stringify(order)}`, LogLevel.DEBUG);

            const r = await axios.put<ApiOrder>(`${this.host}/api/integration/orders`, order, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            addToLog(`<- PUT /api/integration/orders \n ${JSON.stringify(r.data)}`, LogLevel.DEBUG);

            if (r?.data?.orderId) {
                localStorage.setItem("orderId", r.data.orderId);
                localStorage.setItem("order", JSON.stringify(orderStore.order));
                localStorage.setItem("orderId_createdAt", moment().valueOf().toString());
            }

            return r.data;
        }
        catch (e) {
            addToLog(`X <- PUT /api/integration/orders \n ${JSON.stringify(e)}`, LogLevel.ERROR);

            const errAxios = e as AxiosError;
            if (errAxios?.response?.status === 400 && (errAxios?.response?.data as any).errorCode === 3) {
                // ignore
            }
            else if (errAxios?.response) {
                await this.checkAuth(errAxios, this.createOrder, { order });
            }
            else {
                showError((e as Error).toString());
                console.error(e);
            }
        }
        return null;
    }

    public async getOrder(orderId: string): Promise<ApiOrder | null> {
        try {
            const token = await this.getToken();

            const r = await axios.get<ApiOrder>(`${this.host}/api/integration/orders/${orderId}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            return r.data;
        }
        catch (e) {
            const errAxios = e as AxiosError;
            if (errAxios?.response?.status === 401) {
                await this.checkAuth(errAxios, this.getOrder, { orderId });
            }
        }
        return null;
    }


    public async cancelOrder(orderId: string): Promise<boolean> {
        try {
            const token = await this.getToken();

            addToLog(`-> DELETE /api/integration/orders`, LogLevel.DEBUG);

            await axios.delete(`${this.host}/api/integration/orders/${orderId}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            addToLog(`<- DELETE /api/integration/orders`, LogLevel.DEBUG);

            return true;
        }
        catch (e) {
            addToLog(`X <- DELETE /api/integration/orders \n ${JSON.stringify(e)}`, LogLevel.ERROR);

            const errAxios = e as AxiosError;
            if (errAxios?.response?.status === 401) {
                await this.checkAuth(errAxios, this.cancelOrder, { orderId });
            }
        }
        return false;
    }

    public async addOrderLine(orderId: string, orderLine: ApiOrderLine): Promise<ApiOrder | null> {
        try {
            const token = await this.getToken();

            addToLog(`-> PUT /api/integration/orders/${orderId}/orderlines \n ${JSON.stringify(orderLine)}`, LogLevel.DEBUG);

            const r = await axios.put<ApiOrder>(`${this.host}/api/integration/orders/${orderId}/orderlines`, orderLine, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            addToLog(`<- PUT ${this.host}/api/integration/orders/${orderId}/orderlines \n ${JSON.stringify(r.data)}`, LogLevel.DEBUG);

            return r.data;
        }
        catch (e) {
            addToLog(`X <- PUT /api/integration/orders/${orderId}/orderlines \n ${JSON.stringify(e)}`, LogLevel.ERROR);

            const errAxios = e as AxiosError;
            if (errAxios?.response) {
                // empty ministocks
                if (errAxios.status === 409) {
                    showError(t("error.ministockConflict"));
                    const apiOrder = errAxios.response.data as unknown as ApiOrder;
                    apiOrder.lastErrorResponseCode = errAxios.status;
                    return apiOrder;
                }

                await this.checkAuth(errAxios, this.addOrderLine, { orderId, orderLine });
                this.matchUserError(errAxios);
            }
            else {
                showError((e as Error).toString());
                console.error(e);
            }
        }
        return null;
    }

    public async addCombinationOrderLine(orderId: string, orderLine: ApiCombinationOrderLine): Promise<ApiOrder | null> {
        try {
            const token = await this.getToken();

            addToLog(`-> PUT /api/integration/orders/${orderId}/combinations \n ${JSON.stringify(orderLine)}`, LogLevel.DEBUG);

            const r = await axios.put<ApiOrder>(`${this.host}/api/integration/orders/${orderId}/combinations`, orderLine, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            addToLog(`<- PUT /api/integration/orders/${orderId}/combinations \n ${JSON.stringify(r.data)}`, LogLevel.DEBUG);

            return r.data;
        }
        catch (e) {
            addToLog(`X <- PUT /api/integration/orders/${orderId}/combinations \n ${JSON.stringify(e)}`, LogLevel.ERROR);

            const errAxios = e as AxiosError;
            if (errAxios?.response) {
                // empty ministocks
                if (errAxios.status === 409) {
                    showError(t("error.ministockConflict"));
                    const apiOrder = errAxios.response.data as unknown as ApiOrder;
                    apiOrder.lastErrorResponseCode = errAxios.status;
                    return apiOrder;
                }

                await this.checkAuth(errAxios, this.addCombinationOrderLine, { orderId, orderLine });
                this.matchUserError(errAxios);
            }
            else {
                showError((e as Error).toString());
                console.error(e);
            }
        }
        return null;
    }

    public async removeOrderLine(orderId: string, orderLineId: string): Promise<ApiOrder | null> {
        try {
            const token = await this.getToken();

            addToLog(`-> DELETE /api/integration/orders/${orderId}/combinations`, LogLevel.DEBUG);

            const r = await axios.delete<ApiOrder>(`${this.host}/api/integration/orders/${orderId}/orderlines/${orderLineId}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            addToLog(`<- DELETE /api/integration/orders/${orderId}/combinations`, LogLevel.DEBUG);

            return r.data;
        }
        catch (e) {
            addToLog(`X <- DELETE /api/integration/orders/${orderId}/combinations \n ${JSON.stringify(e)}`, LogLevel.ERROR);

            const errAxios = e as AxiosError;
            if (errAxios?.response) {
                await this.checkAuth(errAxios, this.removeOrderLine, { orderId, orderLineId });
                this.matchUserError(errAxios);
            }
            else {
                showError((e as Error).toString());
                console.error(e);
            }
        }
        return null;
    }

    public async addOrderLineQuantity(orderId: string, orderLineId: string): Promise<ApiOrder | null> {
        try {
            const token = await this.getToken();

            addToLog(`-> PUT /api/integration/orders/${orderId}/orderlines/${orderLineId}/quantity`, LogLevel.DEBUG);

            const r = await axios.put<ApiOrder>(`${this.host}/api/integration/orders/${orderId}/orderlines/${orderLineId}/quantity`, {}, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            addToLog(`<- PUT /api/integration/orders/${orderId}/orderlines/${orderLineId}/quantity`, LogLevel.DEBUG);

            return r.data;
        }
        catch (e) {
            addToLog(`X <- PUT /api/integration/orders/${orderId}/orderlines/${orderLineId}/quantity \n ${JSON.stringify(e)}`, LogLevel.ERROR);

            const errAxios = e as AxiosError;
            if (errAxios?.response) {
                // empty ministocks
                if (errAxios.status === 409) {
                    showError(t("error.ministockConflict"));
                    const apiOrder = errAxios.response.data as unknown as ApiOrder;
                    apiOrder.lastErrorResponseCode = errAxios.status;
                    return apiOrder;
                }
                await this.checkAuth(errAxios, this.addOrderLineQuantity, { orderId, orderLineId });
                this.matchUserError(errAxios);
            }
            else {
                showError((e as Error).toString());
                console.error(e);
            }
        }
        return null;
    }

    public async subOrderLineQuantity(orderId: string, orderLineId: string): Promise<ApiOrder | null> {
        try {
            const token = await this.getToken();

            addToLog(`-> DELETE /api/integration/orders/${orderId}/orderlines/${orderLineId}/quantity`, LogLevel.DEBUG);

            const r = await axios.delete<ApiOrder>(`${this.host}/api/integration/orders/${orderId}/orderlines/${orderLineId}/quantity`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            addToLog(`<- DELETE /api/integration/orders/${orderId}/orderlines/${orderLineId}/quantity`, LogLevel.DEBUG);

            return r.data;
        }
        catch (e) {
            addToLog(`X <- PUT /api/integration/orders/${orderId}/orderlines/${orderLineId}/quantity \n ${JSON.stringify(e)}`, LogLevel.ERROR);

            const errAxios = e as AxiosError;
            if (errAxios?.response) {
                // empty ministocks
                if (errAxios.status === 409) {
                    showError(t("error.ministockConflict"));
                    const apiOrder = errAxios.response.data as unknown as ApiOrder;
                    apiOrder.lastErrorResponseCode = errAxios.status;
                    return apiOrder;
                }
                await this.checkAuth(errAxios, this.subOrderLineQuantity, { orderId, orderLineId });
                this.matchUserError(errAxios);
            }
            else {
                showError((e as Error).toString());
                console.error(e);
            }
        }
        return null;
    }

    public async getAllowedPaymentTerminals(cashRegisterId: string): Promise<PaymentTerminal[] | null> {
        try {
            if (!this.host) return null;

            const token = await this.getToken();

            const r = await axios.get<PaymentTerminal[]>(`${this.host}/api/integration/structure/available-payment-terminals?cashRegisterId=${cashRegisterId}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            return r.data;
        }
        catch (e) {
            const errAxios = e as AxiosError;
            if (errAxios?.response) {
                await this.checkAuth(errAxios, this.getAllowedPaymentTerminals, { cashRegisterId });
            }
            else {
                showError((e as Error).toString());
                console.error(e);
            }
        }
        return null;
    }

    public async sendPayment(orderId: string, payment: Payment): Promise<PaymentStatus | null> {
        try {
            const token = await this.getToken();

            addToLog(`-> PUT /api/integration/orders/${orderId}/begin-payment \n ${JSON.stringify(payment)}`, LogLevel.DEBUG);

            const r = await axios.put<PaymentStatus>(`${this.host}/api/integration/orders/${orderId}/begin-payment`, payment, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            const logData = lodash.cloneDeep<PaymentStatus>(r.data);
            delete logData.paymentQrCode;
            addToLog(`<- PUT /api/integration/orders/${orderId}/begin-payment \n ${JSON.stringify(logData)}`, LogLevel.DEBUG);

            return r.data;
        }
        catch (e) {
            addToLog(`X <- PUT /api/integration/orders/${orderId}/begin-payment \n ${JSON.stringify(e)}`, LogLevel.ERROR);

            const errAxios = e as AxiosError<any>;
            if (errAxios?.response?.status === 400 && errAxios?.response?.data?.errorMessage) {
                if (errAxios.response.data.errorCode === 12) {
                    return errAxios.response.data;
                }
                else {
                    if (!this.matchUserError(errAxios))
                        showError(errAxios?.response?.data?.errorMessage);
                }
            }
            else if (errAxios?.response) {
                await this.checkAuth(errAxios, this.sendPayment, { orderId, payment });
            }
            else {
                showError((e as Error).toString());
                console.error(e);
            }
        }
        return null;
    }

    public async getPaymentStatus(paymentId: string): Promise<PaymentStatus | null> {
        try {
            if (!paymentId) return null;

            const token = await this.getToken();

            // addToLog(`-> GET /api/integration/payments/${paymentId}`, LogLevel.DEBUG);

            const r = await axios.get<PaymentStatus>(`${this.host}/api/integration/payments/${paymentId}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            this.matchUserError({response: r} as AxiosError);

            // addToLog(`<- GET /api/integration/payments/${paymentId} \n ${JSON.stringify(r.data)}`, LogLevel.DEBUG);

            return r.data;
        }
        catch (e) {
            // addToLog(`X <- GET /api/integration/payments/${paymentId} \n ${JSON.stringify(e)}`, LogLevel.ERROR);

            const errAxios = e as AxiosError;
            if (errAxios.status === 404) {
                return null;
            }
            else if (errAxios?.response) {
                await this.checkAuth(errAxios, this.getPaymentStatus, { paymentId });
            }
            else {
                if (!this.matchUserError(errAxios)) {
                    showError((e as Error).toString());
                }
            }
        }
        return null;
    }

    public async printReceipt(orderId: string): Promise<PaymentStatus | null> {
        try {
            const token = await this.getToken();

            const r = await axios.post<PaymentStatus>(`${this.host}/api/integration/orders/${orderId}/receipt`, {
                "printOnPaper": true
            },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });

            return r.data;
        }
        catch (e) {
            const errAxios = e as AxiosError;
            if (errAxios?.response) {
                await this.checkAuth(errAxios, this.printReceipt, { orderId });
            }
            else {
                showError((e as Error).toString());
                console.error(e);
            }
        }
        return null;
    }

    public async sendCashRegisterStatus(): Promise<void> {
        try {
            if (!this.host) return;

            const token = await this.getToken();

            const settings = {
                cashRegisterId: localStorage.getItem("cashRegisterId") || "",
                code: localStorage.getItem("code") || "",
                host: localStorage.getItem("host") || "",
                i18nextLng: localStorage.getItem("i18nextLng") || "",
                settings: localStorage.getItem("settings") || "",
                siteId: localStorage.getItem("siteId") || "",
                takeAwayRecommendations_etag: localStorage.getItem("takeAwayRecommendations_etag") ? true : false,
                takeAway_etag: localStorage.getItem("takeAway_etag") ? true : false,
                eatInRecommendations_etag: localStorage.getItem("eatInRecommendations_etag") ? true : false,
                eatIn_etag: localStorage.getItem("eatIn_etag") ? true : false,
                token: localStorage.getItem("token") ? true : false,
            };

            if (settings.cashRegisterId) {
                await axios.post<PaymentStatus>(`${this.host}/api/integration/status/cash-register/${settings.cashRegisterId}`, {
                    serialNumber: Buffer.from(JSON.stringify(settings)).toString('base64'),
                    softwareVersion: pack.version
                },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    });
            }
        }
        catch (e) {
            // ignore
        }
        return;
    }

    public async checkOperationCode(code: string): Promise<boolean> {
        try {
            const token = await this.getToken();

            await axios.post(`${this.host}/api/integration/daily-code/${code}`, {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });

            return true;
        }
        catch (e) {
            // ignore
        }
        return false;
    }

    public async getStyles(): Promise<LayoutStyles | null> {
        try {
            const token = await this.getToken();

            const etag = `?etag=${encodeURIComponent(localStorage.getItem("styles_etag") || "")}`;

            const r = await axios.get<LayoutStyles>(`${this.host}/api/integration/styles${etag}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });

            localStorage.setItem("styles_etag", r.headers.etag || "");
            localStorage.setItem("styles", JSON.stringify(r.data) || "");

            return r.data;
        }
        catch (e) {
            // ignore
        }
        return null;
    }

    public async attachCustomerToOrder(orderId: string, code: string, showErrors: boolean = true): Promise<boolean> {
        try {
            const token = await this.getToken();

            const r = await axios.post<any>(`${this.host}/api/integration/orders/${orderId}/customer`, { code },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });

            if (r.data.customer) {
                orderStore.customer = r.data.customer as ApiCustomer;
            }

            return true;
        }
        catch (e) {
            const axiosErr = e as AxiosError<{ errorCode: number, errorMessage: string }>;
            if (axiosErr.response) {
                if (showErrors) {
                    if (axiosErr.response.status === 401 || axiosErr.response.status === 404) {
                        showAlert(t(`error.customer.16.title`), t(`error.customer.16.text`));
                        return false;
                    }
                    if (this.matchUserError(axiosErr))
                        return false;
                }
                else
                    return false;
            }
        }

        return true;
    }

    public async attachInternalCardToOrder(orderId: string, code: string): Promise<boolean> {
        try {
            const token = await this.getToken();

            const r = await axios.post<any>(`${this.host}/api/integration/orders/${orderId}/internal-card`, { code },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });

            if (r.data.internalCard) {
                orderStore.internalCardObj = r.data.internalCard as ApiInternalCard;
            }

            return true;
        }
        catch (e) {
            const axiosErr = e as AxiosError<{ errorCode: number, errorMessage: string }>;
            if (axiosErr.response) {
                if (axiosErr.response.status === 401 || axiosErr.response.status === 404) {
                    showAlert(t(`error.customer.16.title`), t(`error.customer.16.text`));
                    return false;
                }
                if (this.matchUserError(axiosErr))
                    return false;
            }
        }

        return true;
    }

    public async getPaymentMethods(force: boolean = false): Promise<CashRegisterPaymentMethod[] | null> {
        try {
            const token = await this.getToken();

            const etag = force ? "" : `?etag=${encodeURIComponent(localStorage.getItem("paymentMethods_etag") || "")}`;

            addToLog(`-> GET /api/integration/structure/available-payment-methods`, LogLevel.DEBUG);

            const r = await axios.get<CashRegisterPaymentMethod[]>(`${this.host}/api/integration/structure/available-payment-methods${etag}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });

            localStorage.setItem("paymentMethods_etag", r.headers.etag || "");

            addToLog(`<- GET /api/integration/structure/available-payment-methods \n ${JSON.stringify(r.data)}`, LogLevel.DEBUG);

            return r.data.filter(x => Object.values(PaymentMethod).includes(x.type));
        }
        catch (e) {
            if ((e as AxiosError).status !== 304)
                addToLog(`X <- GET /api/integration/structure/available-payment-methods \n ${JSON.stringify(e)}`, LogLevel.ERROR);
            else
                addToLog(`<- GET /api/integration/structure/available-payment-methods`, LogLevel.DEBUG);
        }
        return null;
    }

    public async cancelPayment(paymentId: string): Promise<boolean> {
        try {
            const token = await this.getToken();

            addToLog(`-> PUT /api/integration/payments/${paymentId}/cancel`, LogLevel.DEBUG);

            await axios.put<CashRegisterPaymentMethod[]>(`${this.host}/api/integration/payments/${paymentId}/cancel`, {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });

            addToLog(`<- PUT /api/integration/payments/${paymentId}/cancel`, LogLevel.DEBUG);

            return true;
        }
        catch (e) {
            addToLog(`X <- PUT ${this.host}/api/integration/payments/${paymentId}/cancel \n ${JSON.stringify(e)}`, LogLevel.ERROR);

            const axiosErr = e as AxiosError;
            this.matchUserError(axiosErr);
        }
        return false;
    }

    public async getFilters(force: boolean = false): Promise<void> {
        try {
            const token = await this.getToken();

            const etag = force ? "" : `?etag=${encodeURIComponent(localStorage.getItem("filter_etag") || "")}`;

            const filter = await axios.get<ApiFilter>(`${this.host}/api/integration/filters${etag}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });

            localStorage.setItem("filter_etag", filter.headers.etag || "");

            if (Array.isArray(filter.data.allergens) && filter.data.allergens.length > 0) {
                localStorage.setItem("filterAllergens", JSON.stringify(filter.data.allergens));
            }

            if (Array.isArray(filter.data.preferences) && filter.data.preferences.length > 0) {
                localStorage.setItem("filterPreferences", JSON.stringify(filter.data.preferences));
            }

            return;
        }
        catch (e) {
            const axiosErr = e as AxiosError;
            this.matchUserError(axiosErr);
        }
        return;
    }

    public async sendLogs(orderId: string): Promise<void> {
        if (!isSendLog()) return;

        try {
            const token = await this.getToken();

            const logs = getLogs();

            await axios.put<ApiFilter>(`${this.host}/api/integration/orders/${orderId}/logs`, logs,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });

            clearLogs();

            return;
        }
        catch (e) {
            const axiosErr = e as AxiosError;
            this.matchUserError(axiosErr);
        }

        clearLogs();

        return;
    }

    public async addHalfAndHalfOrderLine(orderId: string, orderLine: ApiHalfAndHalfOrderLine): Promise<ApiOrder | null> {
        try {
            const token = await this.getToken();

            addToLog(`-> PUT /api/integration/orders/${orderId}/combinations \n ${JSON.stringify(orderLine)}`, LogLevel.DEBUG);

            const r = await axios.put<ApiOrder>(`${this.host}/api/integration/orders/${orderId}/combinations`, orderLine, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            addToLog(`<- PUT /api/integration/orders/${orderId}/combinations`, LogLevel.DEBUG);

            return r.data;
        }
        catch (e) {
            addToLog(`X <- PUT /api/integration/orders/${orderId}/combinations \n ${JSON.stringify(e)}`, LogLevel.ERROR);

            const errAxios = e as AxiosError;
            if (errAxios?.response) {
                // empty ministocks
                if (errAxios.status === 409) {
                    showError(t("error.ministockConflict"));
                    const apiOrder = errAxios.response.data as unknown as ApiOrder;
                    apiOrder.lastErrorResponseCode = errAxios.status;
                    return apiOrder;
                }

                await this.checkAuth(errAxios, this.addHalfAndHalfOrderLine, { orderId, orderLine });
                this.matchUserError(errAxios);
            }
            else {
                showError((e as Error).toString());
                console.error(e);
            }
        }
        return null;
    }
}

const apiOpen = new OpenApi();
export default apiOpen;
